import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, Target, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface SuccessCriteriaSummary {
  signal?: string;
  timeframe?: string;
  validators?: string[];
}

interface Phase11EscalateInputProps {
  onValueChange: (value: string) => void;
  previousCriteria?: SuccessCriteriaSummary;
  className?: string;
}

export const Phase11EscalateInput = ({
  onValueChange,
  previousCriteria,
  className
}: Phase11EscalateInputProps) => {
  const [selectedDimension, setSelectedDimension] = useState<string>("");
  const [constraintText, setConstraintText] = useState("");

  const handleDimensionChange = (dimension: string) => {
    setSelectedDimension(dimension);
    updateCombinedValue(dimension, constraintText);
  };

  const handleConstraintChange = (text: string) => {
    setConstraintText(text);
    updateCombinedValue(selectedDimension, text);
  };

  const updateCombinedValue = (dimension: string, constraint: string) => {
    if (dimension && constraint) {
      const dimensionLabel = getDimensionLabel(dimension);
      onValueChange(`Fokus: ${dimensionLabel}. Abbruchkriterium: ${constraint}`);
    } else if (constraint) {
      onValueChange(`Abbruchkriterium: ${constraint}`);
    } else {
      onValueChange("");
    }
  };

  const getDimensionLabel = (dimension: string) => {
    switch (dimension) {
      case "signal": return "Signal";
      case "timeframe": return "Zeitrahmen";
      case "validation": return "Validierung";
      default: return dimension;
    }
  };

  const hasContext = previousCriteria?.signal || previousCriteria?.timeframe || previousCriteria?.validators?.length;

  // Build radio options from previous criteria
  const radioOptions = [];
  if (previousCriteria?.signal) {
    radioOptions.push({
      value: "signal",
      label: "Signal",
      description: previousCriteria.signal
    });
  }
  if (previousCriteria?.timeframe) {
    radioOptions.push({
      value: "timeframe",
      label: "Zeitrahmen",
      description: `Einhaltung der ${previousCriteria.timeframe}-Frist`
    });
  }
  if (previousCriteria?.validators && previousCriteria.validators.length > 0) {
    radioOptions.push({
      value: "validation",
      label: "Validierung",
      description: previousCriteria.validators.join(", ")
    });
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Sharpened Header */}
      <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-destructive/10">
            <Target className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Das Abbruchsignal
            </h3>
            <p className="text-[15px] text-foreground font-medium leading-relaxed">
              Welches eine Signal MUSS eintreten – sonst war die Entscheidung falsch?
            </p>
            <p className="text-sm text-muted-foreground mt-2 italic">
              Ohne klares Abbruchkriterium entscheidest du nicht – du hoffst.
            </p>
          </div>
        </div>
      </div>

      {/* Context Summary from Primary Question */}
      {hasContext && (
        <div className="p-4 bg-muted/40 border border-border/50 rounded-xl">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <p className="text-sm font-semibold text-foreground">Deine erfassten Kriterien:</p>
              <div className="space-y-1.5 text-sm">
                {previousCriteria?.signal && (
                  <p className="text-foreground/80 line-clamp-2">
                    <span className="font-medium text-foreground">Signal:</span> {previousCriteria.signal}
                  </p>
                )}
                {previousCriteria?.timeframe && (
                  <p className="text-foreground/80">
                    <span className="font-medium text-foreground">Zeitrahmen:</span> {previousCriteria.timeframe}
                  </p>
                )}
                {previousCriteria?.validators && previousCriteria.validators.length > 0 && (
                  <p className="text-foreground/80">
                    <span className="font-medium text-foreground">Validierung:</span> {previousCriteria.validators.join(", ")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Radio Selection - What to prioritize */}
      {radioOptions.length > 0 && (
        <div className="space-y-3">
          <Label className="text-base font-semibold text-foreground">
            Welche Dimension ist nicht verhandelbar?
          </Label>
          <RadioGroup
            value={selectedDimension}
            onValueChange={handleDimensionChange}
            className="space-y-2"
          >
            {radioOptions.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                  selectedDimension === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border/60 hover:border-border hover:bg-muted/30"
                )}
              >
                <RadioGroupItem value={option.value} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">{option.label}</p>
                  <p className="text-sm text-muted-foreground line-clamp-2">{option.description}</p>
                </div>
              </label>
            ))}
          </RadioGroup>
        </div>
      )}

      {/* Constraint Sentence - The hard commitment */}
      <div className="space-y-3">
        <Label 
          htmlFor="constraint-input" 
          className="text-base font-semibold text-foreground flex items-center gap-2"
        >
          <AlertTriangle className="w-4 h-4 text-destructive" />
          Das Abbruchkriterium
          <span className="text-destructive ml-1">*</span>
        </Label>
        
        {/* Constraint frame with visual weight */}
        <div className="border-l-4 border-destructive/60 pl-4 py-1">
          <p className="text-sm font-medium text-foreground mb-2">
            Diese Entscheidung war falsch, wenn folgendes NICHT eintritt:
          </p>
          <Textarea
            id="constraint-input"
            value={constraintText}
            onChange={(e) => handleConstraintChange(e.target.value)}
            placeholder="z.B. Mindestens 2 Kunden treffen eine dokumentierte Go/No-Go-Entscheidung innerhalb von 30 Tagen"
            className="min-h-[100px] resize-none bg-background border-border/60 focus:border-destructive focus:ring-destructive/20 text-[15px] leading-relaxed placeholder:text-muted-foreground/60"
          />
        </div>
      </div>

      {/* Hard hint - no explanation, just consequence */}
      <p className="text-sm text-muted-foreground text-center pt-2 border-t border-border/30">
        Wenn du hier nichts ausschließen kannst, ist die Entscheidung noch nicht reif.
      </p>
    </div>
  );
};
